import { useHandlePermissions } from "0.Mine/1.Hooks/security/permissions";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { AdminLicense, UserLicense } from "0.Mine/3.Interfaces/license";
import AddIcon from "@mui/icons-material/Add";
import { AppBar, Toolbar, Typography, Button, Box } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DynamicDataCaptureModal, { FieldConfig } from "../Reusable/DataCaptureModal";
import GenericTable from "../Reusable/GenericTable";

import AdminSelectModal from "./AdminSelectModal";

const fields: FieldConfig[] = [
  { key: "name", label: "Nome e cognome", type: "text", required: true },
  { key: "email", label: "Email", type: "text", required: true },
  { key: "username", label: "Nome utente", type: "text", required: true },
  { key: "password", label: "Password", type: "text", required: true },
  {
    key: "chosenTemplate",
    label: "Scegli la tipologia",
    type: "select",
    options: ["Social Media Manager", "Pre-Sales", "Sales", "Other"],
    required: true,
  },
];

const editFields: FieldConfig[] = [
    { key: "name", label: "Nome e cognome", type: "text", required: true },
  { key: "email", label: "Email", type: "text", required: true },
  { key: "username", label: "Nome utente", type: "text", required: true },
]

const AdminTable = () => {
  const { t } = useTranslation();
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [isDataCaptureModalOpen, setIsDataCaptureModalOpen] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState("");
  const [data, setData] = useState<UserLicense[]>([]);
  const { createUserLicense, getAllLicenses, getSinglePermission, deletePermission, editPermissions } = useHandlePermissions();
  const { session } = useBusinessContext();
  const [editLicenseModal, setEditLicenseModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<UserLicense>();
  useEffect(() => {
    const fetchData = async () => {
      if (session?.selectedBusiness) {
        try {
          const licenses = await getAllLicenses(session.selectedBusiness.id);
          if(!licenses) {
            enqueueSnackbar("Error fetching licenses", { variant: "error" });
            return;
          }
          setData(licenses);
        } catch (error) {
          console.error("Error fetching licenses:", error);
        }
      }
    };
    fetchData();
  }, [session]);

  if (!session) return <>{t("adminArea.selectABusiness")}</>;

  const openPermissionModal = async (licenseId: string) => {
    setSelectedLicenseId(licenseId);
    setIsPermissionModalOpen(true);
    const licensePermissions = await getSinglePermission(session.selectedBusiness.id, licenseId);
    console.log(licensePermissions);
  };

  const closePermissionModal = () => setIsPermissionModalOpen(false);
  const openDataCaptureModal = () => setIsDataCaptureModalOpen(true);

  const handleSubmitNewLicense = async (formData: any) => {
    const licenseData: Partial<UserLicense> = formData;
    const newLicense: UserLicense = {
      ...formData,
      id: Date.now().toString(),
      registrationDate: new Date().toISOString(),
    };

    setData((prev) => [...prev, newLicense]);
    setIsDataCaptureModalOpen(false);

    const id = await createUserLicense(session.selectedBusiness.id, licenseData);
    if (!id) {
      enqueueSnackbar("Error creating license", { variant: "error" });
    } else {
      enqueueSnackbar("License created successfully", { variant: "success" });
      setSelectedLicenseId(id);
      closePermissionModal();
      openPermissionModal(id);
    }
  };

  const handleSubmitEditLicense = async (formData: any) => {
    const updatedLicense: Partial<UserLicense> = formData;
    const updatedData = data.map((item) => (String(item.id) === selectedLicenseId ? { ...item, ...updatedLicense } : item));
    setData(updatedData);
    setEditLicenseModal(false);
    const success = await editPermissions(session.selectedBusiness.id, selectedLicenseId, updatedLicense);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar position="static" color="default" elevation={1} sx={{ mb: 2 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t("adminArea.manageVendors")}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openDataCaptureModal}>
            {t("adminArea.addVendor")}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, padding: 2, paddingBottom: "100px" }}>
        <GenericTable
          columns={["name", "email", "username", "created_at"]}
          columnLabels={{
            name: t("commonText.name"),
            email: t("commonText.email"),
            username: t("commonText.username"),
            created_at: t("commonText.createdAt"),
          }}
          data={data}
          rowActions={[
            {
              name: "Permessi",
              onClick: (row) => openPermissionModal(row.id),
            },
            {
              name: "Modifica",
              onClick: (row) => {
                if(!row) return;
                setSelectedLicenseId(row.id);
                setSelectedLicense(row);
                setEditLicenseModal(true);
              },
            },
            {
              name: "Elimina",
              onClick: async (row) => {
                await deletePermission(session.selectedBusiness.id, row.id);
                setData((prev) => prev.filter((item) => item.id !== row.id));
                enqueueSnackbar(t("adminArea.vendorDeleted"), { variant: "success" });
              },
            },
          ]}
          noDataMessage={t("adminArea.noVendors")}
          pagination={{
            page: 0,
            pageSize: 3,
            total: data.length,
            onPageChange: () => {},
            onRowsPerPageChange: () => {},
          }}
        />

        <AdminSelectModal
          open={isPermissionModalOpen}
          onClose={closePermissionModal}
          licenseId={selectedLicenseId}
        />
        <DynamicDataCaptureModal
          isOpen={isDataCaptureModalOpen}
          onClose={() => setIsDataCaptureModalOpen(false)}
          onSubmit={handleSubmitNewLicense}
          initialData={{}}
          fields={fields}
          modalTitle="Aggiungi staff"
        />
        <DynamicDataCaptureModal
          isOpen={editLicenseModal}
          onClose={() => setEditLicenseModal(false)}
          onSubmit={handleSubmitEditLicense}
          initialData={selectedLicense}
          fields={editFields}
          modalTitle="Modifica staff"/>
      </Box>
    </Box>
  );
};

export default AdminTable;
